import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
    CBadge,
    CCard,
    CCardBody,
    CCardHeader,
} from '@coreui/react';
import { apiClient } from 'services/Api';
import DataTableComponent from 'components/DataTableComponent';
import SupervisoryAssessmentModal from './supervisoryAssessmentModal';
import ToastNotification from 'components/globals/ToastNotification';

const PendingAssessments = ({
    selectedYear: selectedYearProp,
    availableYears: availableYearsProp,
    onYearChange,
    showYearSelector = true,
}) => {
    const location = useLocation();
    const [assessments, setAssessments] = useState([]);
    const [selectedYearLocal, setSelectedYearLocal] = useState(new Date().getFullYear());
    const [availableYearsLocal, setAvailableYearsLocal] = useState([]);
    const [toast, setToast] = useState(null);

    const selectedYear = selectedYearProp ?? selectedYearLocal;
    const availableYears = availableYearsProp ?? availableYearsLocal;

    const fetchAssessments = useCallback(
        async (year = selectedYear) => {
            try {
                const response = await apiClient.get(`/supervisory-assessments/?year=${year}`);
                const data = response.data || [];
                setAssessments(data);

                // Only manage available years locally if not provided by parent
                if (!availableYearsProp) {
                    const currentYear = new Date().getFullYear();
                    const yearsArr = Array.from(
                        { length: currentYear - 2019 + 1 },
                        (_, i) => 2019 + i
                    ).sort((a, b) => b - a);
                    setAvailableYearsLocal(yearsArr);
                }
            } catch (error) {
                console.error('Error fetching supervisory assessments:', error);
                setToast({
                    title: 'Avaliação de Chefias',
                    message: 'Erro ao carregar avaliações de chefias.',
                    type: 'error',
                });
            }
        },
        [selectedYear, availableYearsProp]
    );

    useEffect(() => {
        fetchAssessments(selectedYear);
    }, [location.pathname, selectedYear, fetchAssessments]);

    // modal state
    const [modalOpen, setModalOpen] = useState(false);
    const [activeAssessment, setActiveAssessment] = useState(null);

    const openModalForAssessment = (row) => {
        setActiveAssessment(row);
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
        setActiveAssessment(null);
    };

    const columns = useMemo(
        () => [
            {
                name: 'Avaliado',
                selector: (row) => row.evaluatee_name,
                sortable: true,
                cell: (row) => (
                    <button
                        type="button"
                        className="btn btn-link p-0 text-decoration-none"
                        onClick={() => openModalForAssessment(row)}
                    >
                        {row.evaluatee_name}
                    </button>
                ),
            },
            {
                name: 'Departamento',
                selector: (row) => row.department_name,
                sortable: true,
            },
            {
                name: 'Estado',
                selector: (row) => (row.concluded ? 'Concluída' : 'Em aberto'),
                sortable: true,
                cell: (row) =>
                    row.concluded ? (
                        <CBadge color="success">Concluída</CBadge>
                    ) : (
                        <CBadge color="secondary">Em aberto</CBadge>
                    ),
            },
        ],
        []
    );

    return (
        <>
            <ToastNotification toast={toast} setToast={setToast} />

            <CCard className="mb-4">
                <CCardHeader>
                    <h3 className="mb-0">Avaliações de Chefias</h3>
                </CCardHeader>
                <CCardBody>
                    <DataTableComponent
                        columns={columns}
                        data={assessments}
                        years={showYearSelector ? availableYears : null}
                        selectedYear={showYearSelector ? selectedYear : undefined}
                        onYearChange={
                            showYearSelector
                                ? (newYear) =>
                                      onYearChange ? onYearChange(newYear) : setSelectedYearLocal(newYear)
                                : null
                        }
                    />
                </CCardBody>
            </CCard>

            <SupervisoryAssessmentModal
                visible={modalOpen}
                onClose={closeModal}
                assessment={activeAssessment}
                onSaved={() => fetchAssessments(selectedYear)}
            />
        </>
    );
};

export default PendingAssessments;
